import { useEffect, useMemo, useRef } from 'react'

import { useApp } from '../../../app/AppContext.jsx'
import { useCatalog } from '../../catalog/context/CatalogContext.jsx'

import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

import {
  normalizeAppUrlState,
  parseAppUrlState,
  serializeAppUrlState,
} from '../utils/appRouteState.js'

export function AppRouteSync() {
  const {
    activeSectionId,
    activeHeroView,
    activeProduct,
    activeBuilding,
    activeTier,
    heroCapabilities,
    replaceNavigationStateHandler,
    projectConfig,
    panelSettings,
  } = useApp()
  const { products, productsStatus } = useCatalog()

  const didInitRef = useRef(false)
  const accessibility = panelSettings?.unit_sheet_accessibility ?? null

  useEffect(() => {
    if (productsStatus !== FETCH_STATUS.READY) return

    function applyLocationState() {
      const normalizedState = normalizeAppUrlState(
        parseAppUrlState(window.location),
        {
          products,
          accessibility,
          defaultHeroView: heroCapabilities.initialView,
          allowedHeroViews: heroCapabilities.availableViews,
        },
      )

      replaceNavigationStateHandler({
        sectionId: normalizedState.sectionId,
        heroView: normalizedState.sectionView,
        productId: normalizedState.activeProduct,
        buildingId: normalizedState.activeBuilding,
        tierId: normalizedState.activeTier,
        scrollTarget: normalizedState.sectionId,
      })

      if (normalizedState.shouldReplaceUrl) {
        const nextLocation = serializeAppUrlState(normalizedState, window.location, {
          products,
          accessibility,
          defaultHeroView: heroCapabilities.initialView,
          allowedHeroViews: heroCapabilities.availableViews,
        })
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${nextLocation.search}${nextLocation.hash}`,
        )
      }

      didInitRef.current = true
    }

    applyLocationState()
    window.addEventListener('popstate', applyLocationState)

    return () => {
      window.removeEventListener('popstate', applyLocationState)
    }
  }, [
    accessibility,
    heroCapabilities.availableViews,
    heroCapabilities.initialView,
    products,
    productsStatus,
    replaceNavigationStateHandler,
  ])

  useEffect(() => {
    if (productsStatus !== FETCH_STATUS.READY) return
    if (!didInitRef.current) return

    const nextLocation = serializeAppUrlState(
      {
        sectionId: activeSectionId,
        sectionView: activeHeroView,
        activeProduct,
        activeBuilding,
        activeTier,
      },
      window.location,
      {
        products,
        accessibility,
        defaultHeroView: heroCapabilities.initialView,
        allowedHeroViews: heroCapabilities.availableViews,
      },
    )

    if (nextLocation.search === window.location.search && nextLocation.hash === window.location.hash) {
      return
    }

    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${nextLocation.search}${nextLocation.hash}`,
    )
  }, [
    accessibility,
    activeBuilding,
    activeHeroView,
    activeProduct,
    activeSectionId,
    activeTier,
    heroCapabilities.availableViews,
    heroCapabilities.initialView,
    products,
    productsStatus,
  ])

  return null
}